import { useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Copy, Loader2 } from "lucide-react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { TagBadge } from "../components/atoms/TagBadge";
import { Typography } from "../components/atoms/Typography";
import { Button } from "../components/atoms/Button";
import { Breadcrumb } from "../components/molecules/Breadcrumb";
import { ConfirmDialog } from "../components/molecules/ConfirmDialog";
import { KbSectionContentPreview } from "../components/molecules/KbSectionContentPreview";
import { dataTableCustomStyles } from "../config/dataTableTheme";
import {
  activateKbSectionVersion,
  getKbSectionGroupDetail,
} from "../services/gameRuleKbSectionService";
import type {
  KbSectionGroupDetail,
  KbSectionInputField,
  KbSectionVersionDetail,
} from "../types/gameRuleKbSection";
import { formatDisplayDateTime } from "../utils/displayDate";
import { withScrollableDataTable } from "../components/organisms/withScrollableDataTable";

const ScrollableDataTable = withScrollableDataTable(DataTable);

function versionContent(version: KbSectionVersionDetail): string {
  const extracted = version.extractedContent?.trim();
  if (extracted) return extracted;
  return version.contentPlain.trim() || "(No content)";
}

function formatVersionDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return formatDisplayDateTime(iso);
  } catch {
    return iso;
  }
}

type PendingActivate = {
  readonly version: KbSectionVersionDetail;
};

export const GameRuleKbSectionDetailPage = () => {
  const navigate = useNavigate();
  const { sectionGroupId } = useParams<{ sectionGroupId: string }>();
  const [detail, setDetail] = useState<KbSectionGroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [pendingActivate, setPendingActivate] = useState<PendingActivate | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  const loadDetail = useCallback(async () => {
    if (!sectionGroupId?.trim()) {
      setError("Section group id is missing.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getKbSectionGroupDetail(sectionGroupId);
      setDetail(data);
      const defaultId =
        data.activeVersionId ??
        (data.versions.length > 0 ? data.versions[data.versions.length - 1].id : null);
      setSelectedVersionId(defaultId);
    } catch (e) {
      setDetail(null);
      setError(e instanceof Error ? e.message : "Unable to load section detail.");
    } finally {
      setLoading(false);
    }
  }, [sectionGroupId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (!actionMessage) return undefined;
    const timer = window.setTimeout(() => setActionMessage(null), 4000);
    return () => window.clearTimeout(timer);
  }, [actionMessage]);

  useEffect(() => {
    if (!copyFeedback) return undefined;
    const timer = window.setTimeout(() => setCopyFeedback(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copyFeedback]);

  const sortedVersions = useMemo(() => {
    if (!detail) return [];
    return [...detail.versions].sort((a, b) => b.version - a.version);
  }, [detail]);

  const selectedVersion = useMemo(() => {
    if (!detail || !selectedVersionId) return null;
    return detail.versions.find((v) => v.id === selectedVersionId) ?? null;
  }, [detail, selectedVersionId]);

  const activeVersion = useMemo(() => {
    if (!detail) return null;
    return detail.versions.find((v) => v.isActive) ?? null;
  }, [detail]);

  const runActivate = useCallback(
    async (version: KbSectionVersionDetail) => {
      if (!sectionGroupId || version.isActive || version.status === "archived") {
        return;
      }
      setActivatingId(version.id);
      setActionError(null);
      setActionMessage(null);
      try {
        await activateKbSectionVersion(sectionGroupId, version.id);
        setActionMessage(`Version ${version.version} is now the active version.`);
        setPendingActivate(null);
        await loadDetail();
        setSelectedVersionId(version.id);
      } catch (e) {
        setActionError(e instanceof Error ? e.message : "Failed to activate version.");
      } finally {
        setActivatingId(null);
      }
    },
    [sectionGroupId, loadDetail],
  );

  const requestActivate = useCallback((version: KbSectionVersionDetail, event?: MouseEvent) => {
    event?.stopPropagation();
    if (version.isActive || version.status === "archived") return;
    setPendingActivate({ version });
  }, []);

  const handleCopyContent = useCallback(async () => {
    if (!selectedVersion) return;
    const text = versionContent(selectedVersion);
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback("Content copied.");
    } catch {
      setCopyFeedback("Unable to copy content.");
    }
  }, [selectedVersion]);

  const fieldColumns: TableColumn<KbSectionInputField>[] = useMemo(
    () => [
      {
        name: "Field",
        selector: (row) => row.name,
        grow: 2,
        minWidth: "140px",
      },
      {
        name: "Type",
        selector: (row) => row.fieldType,
        width: "100px",
      },
      {
        name: "Placeholder",
        selector: (row) => row.docPlaceholder ?? "",
        grow: 2,
        minWidth: "160px",
        cell: (row) => (
          <span className="font-mono text-xs text-gray-700">{row.docPlaceholder ?? "—"}</span>
        ),
      },
      {
        name: "Req.",
        width: "72px",
        cell: (row) => (
          <span className={row.required ? "font-medium text-gray-900" : "text-gray-500"}>
            {row.required ? "Yes" : "No"}
          </span>
        ),
      },
    ],
    [],
  );

  if (loading) {
    return (
      <section className="page-section flex flex-1 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" aria-hidden />
        <span className="sr-only">Loading section detail…</span>
      </section>
    );
  }

  if (error || !detail) {
    return (
      <section className="page-section">
        <button type="button" onClick={() => navigate("/rules")} className="back-link">
          <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
          Back to Game Rule
        </button>
        <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error ?? "Section not found."}
        </div>
      </section>
    );
  }

  const confirmDescription =
    pendingActivate && activeVersion
      ? `Make version ${pendingActivate.version.version} the active version? Version ${activeVersion.version} will become inactive.`
      : pendingActivate
        ? `Make version ${pendingActivate.version.version} the active version?`
        : "";

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Breadcrumb
        items={[
          { label: "Library", to: "/library" },
          { label: "Game Rule", to: "/rules-selection" },
          { label: detail.name },
        ]}
      />

      {/* <button
        type="button"
        onClick={() => navigate("/rules")}
        className="back-link mt-2 shrink-0 self-start"
      >
        <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
        Back to list
      </button> */}

      <header className="page-header shrink-0">
        <div>
          <Typography as="h1" variant="h1">
            {detail.name}
          </Typography>
          <dl className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <div className="flex items-center gap-2">
              <dt className="text-gray-500">Tag</dt>
              <dd>
                {detail.primaryTag ? (
                  <TagBadge label={detail.primaryTag} />
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </dd>
            </div>
            <div className="flex items-center gap-2">
              <dt className="text-gray-500">Active version</dt>
              <dd>
                {detail.activeVersion != null ? (
                  <span className="font-medium text-emerald-700">v{detail.activeVersion}</span>
                ) : (
                  <span className="text-gray-400">None</span>
                )}
              </dd>
            </div>
            <div className="flex items-center gap-2">
              <dt className="text-gray-500">Total versions</dt>
              <dd className="text-gray-700">
                {detail.totalVersions} {detail.totalVersions === 1 ? "version" : "versions"}
              </dd>
            </div>
            {detail.tags.length > 1 ? (
              <div className="flex w-full items-center gap-2 sm:w-auto">
                <dt className="text-gray-500">All tags</dt>
                <dd className="text-gray-700">{detail.tags.join(", ")}</dd>
              </div>
            ) : null}
          </dl>
        </div>
      </header>

      {actionMessage ? (
        <div
          className="mt-4 shrink-0 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
          role="status"
        >
          {actionMessage}
        </div>
      ) : null}
      {actionError ? (
        <div className="mt-4 shrink-0 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {actionError}
        </div>
      ) : null}

      <div className="mt-6 flex min-h-0 flex-1 flex-col gap-6 lg:flex-row lg:gap-6">
        <aside className="w-full shrink-0 lg:w-72 xl:w-80">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Versions
          </h2>
          <ul className="space-y-2 overflow-y-auto lg:max-h-[calc(100vh-14rem)]">
            {sortedVersions.map((version) => {
              const isSelected = version.id === selectedVersionId;
              const isActivating = activatingId === version.id;
              const isArchived = version.status === "archived";
              const canActivate = !version.isActive && !isArchived;
              return (
                <li key={version.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedVersionId(version.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedVersionId(version.id);
                      }
                    }}
                    className={`cursor-pointer rounded-lg border p-3 text-left transition-colors ${
                      isSelected
                        ? "border-primary-400 bg-primary-50/70 ring-1 ring-primary-200"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/80"
                    } ${version.isActive ? "border-l-4 border-l-emerald-500 pl-2.5" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900">Version {version.version}</p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {formatVersionDate(version.createdAt)}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-400">
                          {version.fields.length} {version.fields.length === 1 ? "field" : "fields"}
                        </p>
                      </div>
                      {version.isActive ? (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" aria-hidden />
                          Active
                        </span>
                      ) : isArchived ? (
                        <span className="inline-flex shrink-0 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                          Archived
                        </span>
                      ) : (
                        <span className="inline-flex shrink-0 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
                          Inactive
                        </span>
                      )}
                    </div>
                    {canActivate ? (
                      <div className="mt-3">
                        <Button
                          type="button"
                          variant="secondary"
                          className="w-full px-3 py-1.5 text-xs"
                          disabled={isActivating}
                          onClick={(e) => requestActivate(version, e)}
                        >
                          {isActivating ? "Activating…" : "Set as active"}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {selectedVersion ? (
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Version {selectedVersion.version}
                  </h2>
                  {selectedVersion.description ? (
                    <p className="mt-1 text-sm text-gray-600">{selectedVersion.description}</p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  {copyFeedback ? (
                    <span className="text-xs text-gray-500">{copyFeedback}</span>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    className="gap-1.5 px-3 py-1.5 text-xs"
                    onClick={() => void handleCopyContent()}
                  >
                    <Copy className="h-3.5 w-3.5" aria-hidden />
                    Copy content
                  </Button>
                </div>
              </div>

              {!selectedVersion.isActive && selectedVersion.status !== "archived" ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  This version is not active. Use <strong>Set as active</strong> in the version list
                  to make it the live section for game rule generation.
                </div>
              ) : null}

              <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">Content</h3>
                <div className="max-h-[min(28rem,calc(100vh-22rem))] overflow-auto rounded-lg border border-gray-100 bg-gray-50/80 p-4">
                  <KbSectionContentPreview content={versionContent(selectedVersion)} />
                </div>
              </div>

              {selectedVersion.fields.length > 0 ? (
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="mb-3 text-sm font-semibold text-gray-900">Input fields</h3>
                  <div className="min-h-[180px]">
                    <ScrollableDataTable
                      columns={fieldColumns}
                      data={[...selectedVersion.fields]}
                      customStyles={dataTableCustomStyles}
                      dense
                      fixedHeader
                      height="240px"
                      noDataComponent={<span>No fields</span>}
                      keyField="id"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Select a version to view its content.</p>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={pendingActivate !== null}
        title="Activate this version?"
        description={confirmDescription}
        confirmLabel="Activate version"
        isConfirming={activatingId !== null}
        onCancel={() => {
          if (activatingId === null) setPendingActivate(null);
        }}
        onConfirm={() => {
          if (pendingActivate) void runActivate(pendingActivate.version);
        }}
      />
    </section>
  );
};
